/**
 * `useApexSnapshot` — polls the agent's `/state` endpoint and surfaces
 * the latest snapshot plus connectivity bookkeeping.
 *
 * This is a deliberately simpler cousin of the polling block in
 * `pages/ApexDashboard.tsx`: no realtime fast-path and no probe-history
 * side effect — consumers that want sub-second refresh use the /apex
 * page; everyone else is happy with a 2 s poll.
 *
 * The hook returns:
 *   - `snapshot` : the most recent AgentSnapshot, or `null` while we
 *                  have no data yet.
 *   - `nowMs`    : a 1 s heartbeat tick so consumers can render
 *                  "last poll Xs ago" labels without their own timer.
 *   - `error`    : last fetch error string, or `null`.
 *   - `refresh`  : imperatively re-fetches /state (used after writes
 *                  so the UI doesn't have to wait for the next tick).
 */
import { useCallback, useEffect, useState } from 'react';

import { devApi } from '../lib/dev-api';
import type { AgentSnapshot } from '../lib/types';

const POLL_INTERVAL_MS = 2_000;
const NOW_TICK_INTERVAL_MS = 1_000;

export interface UseApexSnapshotResult {
  snapshot: AgentSnapshot | null;
  nowMs: number;
  error: string | null;
  refresh: () => Promise<void>;
}

export function useApexSnapshot(): UseApexSnapshotResult {
  const [snapshot, setSnapshot] = useState<AgentSnapshot | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [nowMs, setNowMs] = useState(() => Date.now());

  const refresh = useCallback(async (): Promise<void> => {
    const res = await devApi.state();
    if (res.ok) {
      setSnapshot(res.value);
      setError(null);
    } else {
      setError(res.error);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function tick(): Promise<void> {
      const res = await devApi.state();
      if (cancelled) return;
      if (res.ok) {
        setSnapshot(res.value);
        setError(null);
      } else {
        setError(res.error);
      }
    }
    void tick();
    const id = window.setInterval(() => void tick(), POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, []);

  useEffect(() => {
    const id = window.setInterval(() => setNowMs(Date.now()), NOW_TICK_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, []);

  return { snapshot, nowMs, error, refresh };
}
